import {Component, Input, OnInit} from '@angular/core';
import {
    BaseModalComponent,
    EpsonPrinterService,
    LoadingService,
    Organization,
    OrganizationTerminal,
    OrganizationTerminalService,
    PrinterService,
    StarPrinterService,
    ToastService,
    WifiPrinter,
    WifiPrinterService
} from 'brewbill-lib';
import {AlertController, ModalController} from '@ionic/angular';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ConnectPrinterComponent} from '../connect-printer/connect-printer.component';

@Component({
    selector: 'bb-select-printer',
    templateUrl: './select-printer.component.html',
    styleUrls: ['./select-printer.component.scss'],
})
export class SelectPrinterComponent extends BaseModalComponent implements OnInit {
    @Input() organization: Organization;
    @Input() wifiPrinters: WifiPrinter[];
    terminal: OrganizationTerminal;
    connected = false;
    loaded = false;
    connectionType: string;
    bluetooth = false;
    currentPrinter: WifiPrinter;
    formGroup: FormGroup;

    constructor(
        modalController: ModalController,
        private organizationTerminalService: OrganizationTerminalService,
        private epsonPrinterService: EpsonPrinterService,
        private starPrinterService: StarPrinterService,
        private wifiPrinterService: WifiPrinterService,
        private printerService: PrinterService,
        private alertController: AlertController,
        private toastService: ToastService,
        private loadingService: LoadingService,
        private formBuilder: FormBuilder
    ) {
        super(modalController);
    }

    ngOnInit() {
        this.printerService.connectViaBlueTooth();
        const id = localStorage.getItem(`WIFI_PRINTER_${this.organization.id}`);
        this.currentPrinter = !!id ? this.wifiPrinters.find(w => w.id === parseInt(id, 10)) : null;

        this.subscribe(this.printerService.connected.subscribe(c => {
            if (this.loaded && !this.connected && !!c) {
                this.close();
            }
            this.connected = !!c;
            this.connectionType = c;
        }));

        const columns = this.printerService.columns;
        this.formGroup = this.formBuilder.group({
            columns: [columns, [Validators.required, Validators.min(0)]]
        });
    }

    disconnect() {
        this.epsonPrinterService.disconnect();
        this.close();
    }

    async connect() {
        this.loadingService.present('Connecting printer.');
        const connected = await this.printerService.initializeWifiPrinter(this.currentPrinter, true);
        this.loadingService.dismiss();
        if (connected) {
            this.wifiPrinterService.setCurrent(this.currentPrinter);
            await this.close();
        } else {
            await this.toastService.error(`Could not connect to ${this.currentPrinter.name} at ${this.currentPrinter.ipAddress}`);
        }
    }

    save() {
        this.starPrinterService.setColumns(this.formGroup.controls.columns.value);
        this.close();
    }

    async openEditPrinter(event, printer: WifiPrinter) {
        event.stopPropagation();
        const modal = await this.modalController.create({
            component: ConnectPrinterComponent,
            componentProps: {
                printer,
                organization: this.organization
            }
        });

        await modal.present();

        await modal.onDidDismiss().then((dataReturned) => {
            if (dataReturned !== null && dataReturned.data != null) {
                const t = new WifiPrinter(dataReturned.data);
                if (!printer) {
                    this.wifiPrinters.push(t);
                } else {
                    this.wifiPrinters[this.wifiPrinters.findIndex(c => c.id === t.id)] = t;
                }
                this.currentPrinter = t;
            }
        });
    }

    async deletePrinter(event, printer: WifiPrinter) {
        event.stopPropagation();

        const alert = await this.alertController.create({
            cssClass: 'brewbill-alert',
            header: 'Confirm Delete Printer',
            message: `Are you sure you would like to delete ${printer.name}?`,
            buttons: [
                {
                    text: 'No',
                    role: 'cancel'
                }, {
                    text: 'Yes',
                    handler: async () => {
                        this.wifiPrinterService.delete(printer.id).subscribe(() => {
                            if (!!this.currentPrinter && this.currentPrinter.id === printer.id) {
                                this.currentPrinter = null;
                                this.wifiPrinterService.setCurrent(null);
                            }
                            this.wifiPrinters.splice(this.wifiPrinters.findIndex(p => printer.id === p.id), 1);
                        });
                    }
                }
            ]
        });

        await alert.present();
    }
}
